import { Component, ElementRef, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert';
import {
  CompraBeneficiosService,
  CompraBeneficioCompleto,
  AtualizarItemRequest,
} from './compra-beneficios.service';
import { ErroService } from 'src/app/service/erros/erro.service';
import { Observable } from 'rxjs';
import { Crypto } from 'src/app/service/classes/crypto/crypto';
import { Router } from '@angular/router';

declare global {
  interface Window {
    bootstrap: any;
  }
}

@Component({
  templateUrl: './compra-beneficios.component.html',
  styleUrls: ['./compra-beneficios.component.css'],
})
export class CompraBeneficiosComponent implements OnInit, AfterViewInit {
  form: FormGroup;
  aba: 'executar' | 'historico' = 'executar';

  beneficios: any[] = [];

  ativos: any[] = [];
  afastados: any[] = [];

  totalGeral = 0;
  todosAtivosSelecionados = false;
  todosAfastadosSelecionados = false;

  historico: any[] = [];
  detalhe: { itens: any[]; total: number } | null = null;

  detalheOriginal: { itens: any[]; total: number } | null = null;
  detalheModificado: boolean = false;
  codigoCompraAtual: number | null = null;
  carregandoHistorico: boolean = false;
  carregandoLista: boolean = false;
  carregandoDetalhe: boolean = false;

  @ViewChild('modalDetalhe') modalDetalhe: ElementRef;
  modal: any;

  ngAfterViewInit(): void {
    this.inicializarModal();
  }

  private inicializarModal(): void {
    const el = this.modalDetalhe?.nativeElement as HTMLElement;

    if (typeof window !== 'undefined' && (window as any).bootstrap) {
      try {
        const bootstrap = (window as any).bootstrap;
        this.modal =
          bootstrap.Modal.getInstance(el) ||
          new bootstrap.Modal(el, { backdrop: 'static', keyboard: true });
      } catch (error) {}
    }
  }

  constructor(
    public fb: FormBuilder,
    public svc: CompraBeneficiosService,
    public spinner: NgxSpinnerService,
    public erroService: ErroService,
    public router: Router
  ) {
    this.verificarAutenticacao();
  }

  ngOnInit(): void {
    this.inicializarForm();
    this.carregarBeneficios();
  }

  validaCompetencia(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return { required: true };
    }

    const regex = /^(0[1-9]|1[0-2])\/\d{4}$/;

    if (!regex.test(value)) {
      return { formatoInvalido: true };
    }

    return null;
  }

  private obterCredencialBase64(): string {
    try {
      const utilCriptografado = localStorage.getItem('util');
      if (!utilCriptografado) return '';
      const utilDecriptado = new Crypto().decryptUsingAES256(utilCriptografado);
      const localStorageObj = JSON.parse(utilDecriptado);
      return localStorageObj?.CredencialBase64 ?? '';
    } catch {
      return '';
    }
  }

  private verificarAutenticacao(): void {
    const credencialBase64 = this.obterCredencialBase64();
    if (!credencialBase64) {
      this.router.navigate(['/login']);
      return;
    }
  }

  inicializarForm(): void {
    this.form = this.fb.group({
      competencia: ['', [Validators.required, this.validaCompetencia.bind(this)]],
      beneficio: ['', Validators.required],
    });
  }

  compToAnoMes(): string {
    const [mm, aaaa] = (this.form.value.competencia as string).split('/');
    return `${aaaa}${mm}`;
  }

  marcarCamposComoTocados(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }

  temErro(campo: string): boolean {
    const control = this.form.get(campo);
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }

  getMensagemErro(campo: string): string {
    const control = this.form.get(campo);

    if (!control || !control.errors) return '';

    const errors = control.errors;

    if (errors['required']) return 'Campo obrigatório';
    if (errors['formatoInvalido']) return 'Formato inválido. Use MM/AAAA';
    if (errors['mesInvalido']) return 'Mês deve estar entre 01 e 12';

    return 'Campo inválido';
  }

  buscarBeneficio(): void {
    this.carregandoLista = true;
    this.spinner.show();
    const anoMes = this.compToAnoMes();
    const codBenef = Number(this.form.value.beneficio);

    const sigla = this.svc.sigla;
    const filial = this.svc.filial;

    this.svc.listar(sigla, anoMes, codBenef, filial).subscribe({
      next: (response: any) => {
        this.spinner.hide();
        this.carregandoLista = false;

        const lista = response?.result || response || [];
        this.processarFuncionarios(lista);
        this.aba = 'executar';
      },
      error: (e) => {
        this.carregandoLista = false;
        console.error('Erro na busca:', e);
        this.erroService.retornoErro(e);
        this.ativos = [];
        this.afastados = [];
        this.totalGeral = 0;
        const mensagemErro = e.error?.msg;
        this.spinner.hide();
        Swal('Erro', mensagemErro, 'error');
      },
    });
  }

  brToNumber(v: any): number {
    if (typeof v === 'number') return v;
    if (v == null || v === '') return 0;

    const str = String(v).trim();

    if (/^-?\d+\.?\d*$/.test(str)) {
      return Number(str);
    }

    const cleanStr = str.replace(/\./g, '').replace(',', '.');

    return Number(cleanStr) || 0;
  }

  processarFuncionarios(lista: any[]): void {
    const mapItem = (x: any) => {
      const valorDiaNum = this.brToNumber(x.Valor);
      const qtdSugerida = Number(x.QtdSugerida) || 0;

      return {
        ...x,
        DescLotacao: (x.DescLotacao || '').trim(),
        _valorDiaNum: valorDiaNum,
        QtdSugerida: qtdSugerida,
        _selecionado: false,
        _total: valorDiaNum * qtdSugerida,
      };
    };

    const todos = (lista ?? []).map(mapItem);

    this.ativos = todos.filter((x) => (x.Afastado ?? 'N') !== 'S');
    this.afastados = todos.filter((x) => (x.Afastado ?? 'N') === 'S');

    this.todosAtivosSelecionados = false;
    this.todosAfastadosSelecionados = false;
    this.recalcularTotais();
  }

  trackByMat = (_: number, row: any) => row.Mat;

  toggleSelecionarTodos(tipo: 'A' | 'F'): void {
    if (tipo === 'A') {
      this.todosAtivosSelecionados = !this.todosAtivosSelecionados;
      this.ativos.forEach((a) => (a._selecionado = this.todosAtivosSelecionados));
    } else {
      this.todosAfastadosSelecionados = !this.todosAfastadosSelecionados;
      this.afastados.forEach((a) => (a._selecionado = this.todosAfastadosSelecionados));
    }
    this.recalcularTotais();
  }

  recalcularLinhaDetalhe(item: any) {
    const qtdDia = Number(item.QtdDia) || 0;
    const diasNecessarios = Number(item.DiasNecessarios) || 0;
    const valorDiaCalculo = Number(item._valorDiaCalculo) || 0;

    const novaQuantidade = qtdDia * diasNecessarios;

    const novoValorTotal = novaQuantidade * valorDiaCalculo;

    item.QtdSugerida = novaQuantidade;
    item.Quantidade = item.QtdSugerida;
    item.Valor = novoValorTotal;

    item._editado = diasNecessarios !== item.DiasNecessariosOriginal;

    this.recalcularTotalDetalhe();

    this.verificarModificacoes();
  }

  limparSelecoes(): void {
    [...this.ativos, ...this.afastados].forEach((x) => (x._selecionado = false));
    this.todosAtivosSelecionados = false;
    this.todosAfastadosSelecionados = false;
    this.recalcularTotais();
  }

  temSelecao(): boolean {
    return [...this.ativos, ...this.afastados].some((x) => x._selecionado);
  }

  podeEfetivar(): boolean {
    return [...this.ativos, ...this.afastados].some(
      (x) => x._selecionado && Number(x.QtdSugerida) > 0
    );
  }

  calcTotalLinha(necessarios: number, valorDia: number): number {
    return (necessarios || 0) * (valorDia || 0);
  }

  recalcularLinha(f: any): void {
    const dias = Number(f.DiasNecessarios) || 0;
    const qtdPorDia = Number(f.QtdDia) || 0;

    f.QtdSugerida = dias * qtdPorDia;

    f._total = (Number(f._valorDiaNum) || 0) * f.QtdSugerida;

    this.recalcularTotais();
  }

  recalcularTotais(): void {
    const linhas = [...this.ativos, ...this.afastados];
    this.totalGeral = linhas
      .filter((x) => x._selecionado)
      .reduce((acc, x) => acc + (Number(x._total) || 0), 0);

    this.todosAtivosSelecionados =
      this.ativos.length > 0 && this.ativos.every((x) => x._selecionado);
    this.todosAfastadosSelecionados =
      this.afastados.length > 0 && this.afastados.every((x) => x._selecionado);
  }

  efetivarCompra(): void {
    if (this.form.invalid || !this.podeEfetivar()) {
      if (this.form.invalid) this.marcarCamposComoTocados();
      return;
    }

    Swal({
      title: 'Aviso',
      text: `Deseja efetivar a compra no valor total de R$ ${this.totalGeral.toFixed(2)}?`,
      icon: 'warning',
      buttons: ['Cancelar', 'Confirmar'],
    }).then((confirm) => {
      if (confirm) {
        this.processarEfetivacao();
      }
    });
  }

  mascararCompetencia(ev: Event) {
    const ctrl = this.form.get('competencia');
    let v = (ev.target as HTMLInputElement).value || '';

    v = v.replace(/\D/g, '').slice(0, 6);

    if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);

    (ev.target as HTMLInputElement).value = v;
    ctrl?.setValue(v, { emitEvent: false });
  }

  normalizarCompetencia() {
    const ctrl = this.form.get('competencia');
    let v = (ctrl?.value || '').toString();

    v = v.replace(/\D/g, '').slice(0, 6);
    if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
    ctrl?.setValue(v, { emitEvent: true });

    const m = Number(v.slice(0, 2));
    if (m && (m < 1 || m > 12)) {
      ctrl?.setErrors({ comp: true });
    }
  }

  processarEfetivacao(): void {
    this.spinner.show();

    const anoMes = this.compToAnoMes();
    const codBenef = Number(this.form.value.beneficio);

    const selecionados = [...this.ativos, ...this.afastados].filter(
      (x) => x._selecionado && Number(x.QtdSugerida) > 0
    );

    const itensFormatados = selecionados.map((item) => ({
      Numord: item.NumOrd,
      Quantidade: Number(item.QtdSugerida) || 0,
      DiasNecessarios: item.DiasNecessarios,
      ValorDia: item.ValorDia,
    }));

    const compraCompleta: CompraBeneficioCompleto = {
      Sigla: this.svc.sigla,
      Empfil: Number(this.svc.filial),
      CodigoBeneficio: codBenef,
      AnoMes: anoMes,
      CodUsuario: this.svc.loginUsuario,
      itens: itensFormatados,
    };

    this.svc.inserirCompraBeneficioCompleto(compraCompleta).subscribe({
      next: (res: any) => {
        this.spinner.hide();

        if (res?.success) {
          Swal('Sucesso', 'Compra efetivada com sucesso!', 'success');
          this.limparDepoisDaCompra();
          this.abrirHistoricoBeneficios();
        } else {
          Swal('Atenção', res?.msg || 'Compra processada com observações.', 'warning');
          this.limparDepoisDaCompra();
          this.abrirHistoricoBeneficios();
        }
      },
      error: (e) => {
        const mensagemErro = e.error?.msg;

        this.spinner.hide();
        Swal('Erro', mensagemErro, 'error');
      },
    });
  }

  limparDepoisDaCompra(): void {
    this.ativos = [];
    this.afastados = [];
    this.totalGeral = 0;
    this.limparSelecoes();
  }

  abrirHistoricoBeneficios(): void {
    this.aba = 'historico';
    this.carregandoHistorico = true;
    this.spinner.show();

    if (
      !this.form.value.competencia ||
      this.form.get('competencia')?.invalid ||
      !this.form.value.beneficio
    ) {
      this.spinner.hide();
      this.carregandoHistorico = false;
      Swal(
        'Aviso',
        'Selecione uma competência e um benefício para visualizar o histórico.',
        'warning'
      );
      return;
    }

    const anoMes = this.compToAnoMes();
    const codBenef = Number(this.form.value.beneficio);

    this.svc.listarHistoricoCompraBeneficio(anoMes, codBenef).subscribe({
      next: (response: any) => {
        this.spinner.hide();
        this.carregandoHistorico = false;

        const dados = response?.result || [];

        this.historico = dados.map((x: any) => {
          const codigoBeneficio = x.CodigoBeneficio ?? x.codigoBeneficio ?? x.CodBeneficio ?? null;

          const nomeBeneficio = this.obterNomeBeneficio(codigoBeneficio);

          let dataHoraFormatada = null;
          if (x.DataHora) {
            try {
              dataHoraFormatada = this.formatarDataAPI(x.DataHora);
            } catch (e) {
              console.warn('Erro ao formatar data:', x.DataHora, e);
              dataHoraFormatada = x.DataHora;
            }
          }

          let totalValor = 0;
          if (x.ValorTotal !== undefined && x.ValorTotal !== null) {
            totalValor = this.brToNumber(x.ValorTotal);
          }

          return {
            CodigoCompraBeneficio: x.CodigoCompraBeneficio ?? x.codigoCompraBeneficio ?? null,
            AnoMes:
              x.AnoMes?.length === 6
                ? `${x.AnoMes.slice(4, 6)}/${x.AnoMes.slice(0, 4)}`
                : x.AnoMes || '',
            CodigoBeneficio: codigoBeneficio,
            NomeBeneficio: nomeBeneficio,
            TotalQuantidade: Number(x.Quantidade ?? x.totalQuantidade ?? 0),
            TotalValor: totalValor,
            CodUsuario: x.CodUsuario ?? x.codUsuario ?? '',
            DataHora: dataHoraFormatada,
            DataHoraOriginal: x.DataHora,
            Beneficio: x.Beneficio ?? nomeBeneficio,
          };
        });

        this.historico.sort((a, b) => {
          const dataA = new Date(a.DataHora || 0).getTime();
          const dataB = new Date(b.DataHora || 0).getTime();
          return dataB - dataA;
        });

        if (this.historico.length === 0) {
          Swal('Info', 'Nenhuma compra encontrada para os filtros selecionados.', 'info');
        }
      },
      error: (err) => {
        const mensagemErro = err.error?.msg;

        this.spinner.hide();
        this.carregandoHistorico = false;
        Swal('Erro', mensagemErro, 'error');
      },
    });
  }

  private formatarDataAPI(dataString: string): string {
    if (!dataString) return '';

    try {
      const partes = dataString.split('  ');
      if (partes.length === 2) {
        const dataPart = partes[0];
        const horaPart = partes[1];

        const meses: { [key: string]: string } = {
          Jan: '01',
          Feb: '02',
          Mar: '03',
          Apr: '04',
          May: '05',
          Jun: '06',
          Jul: '07',
          Aug: '08',
          Sep: '09',
          Oct: '10',
          Nov: '11',
          Dec: '12',
        };

        const dataPartes = dataPart.split(' ');
        if (dataPartes.length === 3) {
          const mesAbrev = dataPartes[0];
          const dia = dataPartes[1].padStart(2, '0');
          const ano = dataPartes[2];

          const mes = meses[mesAbrev] || '01';

          let hora24 = horaPart;
          if (horaPart.includes('PM') && !horaPart.startsWith('12')) {
            const horaParts = horaPart.replace('PM', '').split(':');
            const hora = parseInt(horaParts[0]) + 12;
            hora24 = `${hora}:${horaParts[1]}`;
          } else if (horaPart.includes('AM')) {
            hora24 = horaPart.replace('AM', '');
          } else {
            hora24 = horaPart.replace(/[AP]M/, '');
          }
          hora24 = hora24.trim();
          if (hora24.length === 4) {
            hora24 = `0${hora24}`;
          }

          return `${ano}-${mes}-${dia}T${hora24}:00`;
        }
      }

      return dataString;
    } catch (e) {
      console.warn('Erro ao converter data:', dataString, e);
      return dataString;
    }
  }

  private obterNomeBeneficio(codigo: number): string {
    const mapeamentoBeneficios: { [key: number]: string } = {
      1: 'VALE TRANSPORTE',
      2: 'VALE ALIMENTAÇÃO',
      3: 'VALE REFEIÇÃO',
    };

    return mapeamentoBeneficios[codigo] || `Benefício ${codigo}`;
  }

  private ensureModalInstance(): any {
    const el = this.modalDetalhe?.nativeElement as HTMLElement;

    if (!el) {
      console.warn('[MODAL] Elemento não encontrado');
      return null;
    }

    if (typeof window !== 'undefined' && (window as any).bootstrap) {
      try {
        const bootstrap = (window as any).bootstrap;
        this.modal =
          bootstrap.Modal.getInstance(el) ||
          new bootstrap.Modal(el, { backdrop: 'static', keyboard: true });

        return this.modal;
      } catch (error) {}
    }

    const g: any = window as any;
    if (g?.$?.fn?.modal) {
      g.$(el).modal({ backdrop: 'static', keyboard: true });

      return null;
    }
    return null;
  }

  maskComp(anoMes: string): string {
    return anoMes?.length === 6 ? `${anoMes.slice(4)}/${anoMes.slice(0, 4)}` : anoMes;
  }

  baixarArquivoDetalhamento(): void {
    if (!this.detalhe || !this.detalhe.itens || this.detalhe.itens.length === 0) {
      console.error('Nenhum dado disponível para download');
      Swal('Aviso', 'Não há dados para gerar o arquivo.', 'warning');
      return;
    }

    try {
      let conteudoArquivo = '0200\n';

      this.detalhe.itens.forEach((item) => {
        const cpf = item.Cpf || item.Mat || '00000000000';
        const quantidade = item.DiasNecessarios || '0';

        let valorDia = item.ValorDia;

        if (!valorDia || valorDia === 0) {
          const valorTotal = Number(item.Valor || item.ValorTotal || 0);
          const dias = Number(item.DiasNecessarios || item.Quantidade || 1);
          valorDia = dias > 0 ? valorTotal / dias : 0;
        }

        const valorFormatado = this.formatarValorParaArquivo(valorDia);
        const nome = this.formatarNomeParaArquivo(item.Nome || '');

        const linha = `${cpf}|${quantidade}|${valorFormatado}|${nome}`;
        conteudoArquivo += linha + '\n';
      });

      const blob = new Blob([conteudoArquivo], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      const nomeArquivo = this.gerarNomeArquivoCompetencia();
      link.download = nomeArquivo;

      link.click();

      window.URL.revokeObjectURL(url);

      Swal('Sucesso', `Arquivo ${nomeArquivo} baixado com sucesso!`, 'success');
    } catch (error) {
      console.error('❌ Erro ao baixar arquivo de detalhamento:', error);
      Swal('Erro', 'Não foi possível gerar o arquivo.', 'error');
    }
  }

  private gerarNomeArquivoCompetencia(): string {
    let competencia = '';

    if (this.form && this.form.value.competencia) {
      competencia = this.form.value.competencia;
    } else if (this.detalhe && this.detalhe.itens && this.detalhe.itens.length > 0) {
      const primeiroItem = this.detalhe.itens[0];
      if (primeiroItem.AnoMes) {
        const anoMes = primeiroItem.AnoMes;

        if (anoMes.length === 6) {
          const mes = anoMes.substring(4, 6);
          const ano = anoMes.substring(0, 4);
          competencia = `${mes}/${ano}`;
        }
      }
    } else {
      const data = new Date();
      const mes = (data.getMonth() + 1).toString().padStart(2, '0');
      const ano = data.getFullYear().toString();
      competencia = `${mes}/${ano}`;
    }

    const numerosCompetencia = competencia.replace(/\D/g, '');

    if (numerosCompetencia.length === 6) {
      const mes = numerosCompetencia.substring(0, 2);
      const ano = numerosCompetencia.substring(2, 6);
      return `VT${mes}${ano}.txt`;
    } else if (numerosCompetencia.length === 4) {
      const data = new Date();
      const mes = (data.getMonth() + 1).toString().padStart(2, '0');
      return `VT${mes}${numerosCompetencia}.txt`;
    } else {
      const data = new Date();
      const mes = (data.getMonth() + 1).toString().padStart(2, '0');
      const ano = data.getFullYear().toString();
      return `VT${mes}${ano}.txt`;
    }
  }

  private formatarValorParaArquivo(valor: number): string {
    return valor.toFixed(2).replace('.', ',');
  }

  private formatarNomeParaArquivo(nome: string): string {
    return nome.trim().substring(0, 80).padEnd(80, ' ');
  }

  formatarData(dataString: string): string {
    if (!dataString) return '';

    try {
      const data = new Date(dataString);

      if (isNaN(data.getTime())) {
        return dataString;
      }

      return data.toLocaleString('pt-BR');
    } catch (e) {
      console.warn('Erro ao formatar data:', dataString, e);
      return dataString;
    }
  }

  abrirDetalhe(h: any): void {
    try {
      const codigo =
        h?.Codigo ??
        h?.codigo ??
        h?.CodCompraBeneficio ??
        h?.codCompraBeneficio ??
        h?.CodigoCompraBeneficio ??
        h?.codigoCompraBeneficio ??
        null;

      if (codigo == null) {
        return;
      }

      this.carregandoDetalhe = true;
      this.codigoCompraAtual = codigo;
      const anoMes = this.compToAnoMes();
      this.spinner.show();

      this.abrirModal();

      this.svc.listarDetalheCompraBeneficio(anoMes, codigo).subscribe({
        next: (response: any) => {
          this.spinner.hide();

          this.carregandoDetalhe = false;

          const payload = response?.result || response;

          const arr: any[] = Array.isArray(payload?.itens)
            ? payload.itens
            : Array.isArray(payload?.Itens)
            ? payload.Itens
            : Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload)
            ? payload
            : [];

          const itensMapeados = arr.map((item, idx) => {
            const qtdSugerida = Number(item.Quantidade || item.QtdSugerida || 0);
            const valorTotal = Number(item.Valor || item.ValorTotal || 0);
            const diasNecessarios = Number(item.DiasNecessarios || 0);
            const qtdDia = Number(item.QtdDia || 0);

            const valorDiaExibicao = Number(item.ValorDia || 0);

            const valorDiaCalculo = qtdSugerida > 0 ? valorTotal / qtdSugerida : 0;

            const qtdDiaCalculado =
              qtdDia || (diasNecessarios > 0 ? qtdSugerida / diasNecessarios : 0);

            return {
              ...item,
              Mat: item.Mat ?? item.matricula ?? item.Matricula ?? item.Numord,
              Nome: item.Nome ?? item.nome,
              DescLotacao: (item.DescLotacao ?? item.descLotacao ?? item.departamento ?? '')
                .toString()
                .trim(),
              ValorDia: valorDiaExibicao,
              _valorDiaCalculo: valorDiaCalculo,
              QtdDia: qtdDiaCalculado,
              DiasNecessarios: diasNecessarios,
              DiasNecessariosOriginal: diasNecessarios,
              QtdSugeridaOriginal: qtdSugerida,
              QtdSugerida: qtdSugerida,
              Quantidade: qtdSugerida,
              ValorTotal: valorTotal,
              ValorTotalOriginal: valorTotal,
              NumOrd: item.NumOrd ?? item.numOrd ?? item.Numord ?? idx + 1,
              CodBeneficio: item.CodBeneficio ?? item.codBeneficio ?? item.CodigoBeneficio,
              _editado: false,
            };
          });

          const total = itensMapeados.reduce((acc, i) => acc + (Number(i.ValorTotal) || 0), 0);

          this.detalheOriginal = {
            itens: JSON.parse(JSON.stringify(itensMapeados)),
            total,
          };
          this.detalhe = { itens: itensMapeados, total };
          this.detalheModificado = false;
        },
        error: (err) => {


          this.carregandoDetalhe = false;
          this.detalhe = { itens: [], total: 0 };
          this.detalheOriginal = null;
          this.detalheModificado = false;
          const mensagemErro = err.error?.msg;

          this.spinner.hide();
          Swal('Erro', mensagemErro, 'error');
        },
      });
    } catch (e) {
      this.spinner.hide();

      this.carregandoDetalhe = false;
      console.error('Erro no método abrirDetalhe:', e);
    }
  }

  atualizarQuantidadeDetalhe(item: any, novaQuantidade: number): void {
    if (novaQuantidade < 0) return;

    const quantidadeOriginal = item.QtdSugeridaOriginal;
    item.QtdSugerida = novaQuantidade;
    item.ValorTotal = item.ValorDia * novaQuantidade;
    item._editado = novaQuantidade !== quantidadeOriginal;

    this.recalcularTotalDetalhe();
    this.verificarModificacoes();
  }

  recalcularTotalDetalhe() {
    if (!this.detalhe || !this.detalhe.itens) return;

    this.detalhe.total = this.detalhe.itens.reduce((acc, item) => acc + Number(item.Valor || 0), 0);
  }

  verificarModificacoes(): void {
    if (!this.detalhe || !this.detalheOriginal) {
      this.detalheModificado = false;
      return;
    }

    this.detalheModificado = this.detalhe.itens.some((item) => item._editado);
  }

  excluirItemDetalhe(item: any): void {
    if (!this.detalhe || this.detalhe.itens.length <= 1 || !this.codigoCompraAtual) {
      Swal('Aviso', 'Não é possível excluir o único item da compra.', 'warning');
      return;
    }

    Swal({
      title: 'Confirmar exclusão',
      text: `Deseja excluir o item de ${item.Nome}?`,
      icon: 'warning',
      buttons: ['Cancelar', 'Excluir'],
      dangerMode: true,
    }).then((confirm) => {
      if (confirm) {
        this.spinner.show();

        this.svc.excluirItemCompra(this.codigoCompraAtual, item.NumOrd).subscribe({
          next: () => {
            this.spinner.hide();

            const index = this.detalhe.itens.findIndex((i) => i.NumOrd === item.NumOrd);
            if (index > -1) {
              this.detalhe.itens.splice(index, 1);
              this.recalcularTotalDetalhe();
              this.verificarModificacoes();
            }

            Swal('Sucesso', 'Item excluído com sucesso!', 'success');
          },
          error: (err) => {
            const mensagemErro = err.error?.msg;

            this.spinner.hide();
            Swal('Erro', mensagemErro, 'error');
          },
        });
      }
    });
  }

  async atualizarCompra(): Promise<void> {
    if (!this.detalheModificado || !this.detalhe || !this.codigoCompraAtual) {
      return;
    }

    const confirm = await Swal({
      title: 'Confirmar atualização',
      text: 'Deseja salvar as alterações realizadas?',
      icon: 'warning',
      buttons: ['Cancelar', 'Atualizar'],
    });

    if (!confirm) return;

    this.spinner.show();

    try {
      const itensModificados = this.detalhe.itens.filter((item) => item._editado);

      for (const item of itensModificados) {
        const body = {
          Sigla: this.svc.sigla,
          AnoMes: this.compToAnoMes(),
          Empfil: Number(this.svc.filial),
          Numord: item.NumOrd,
          ValorDia: item.ValorDia,
          DiasNecessarios: item.DiasNecessarios,
          CodigoCompraBeneficio: this.codigoCompraAtual,
          Quantidade: item.QtdSugerida,
        };

        await this.svc.atualizarItemCompra(body).toPromise();

        item.QtdSugeridaOriginal = item.QtdSugerida;
        item.DiasNecessariosOriginal = item.DiasNecessarios;
        item.ValorTotalOriginal = item.Valor;
        item._editado = false;
      }

      if (this.detalheOriginal) {
        this.detalheOriginal.itens = JSON.parse(JSON.stringify(this.detalhe.itens));
        this.detalheOriginal.total = this.detalhe.total;
      }

      this.detalheModificado = false;
      this.spinner.hide();

      Swal('Sucesso', 'Compra atualizada com sucesso!', 'success');
    } catch (err) {
      const mensagemErro = err.error?.msg;

      this.spinner.hide();
      Swal('Erro', mensagemErro, 'error');
    }
  }

  abrirModal(): void {
    if (this.modal?.show) {
      this.modal.show();
    } else {
      this.ensureModalInstance();
      if (this.modal?.show) {
        this.modal.show();
      } else {
        const el = this.modalDetalhe?.nativeElement as HTMLElement;
        const g: any = window as any;
        if (g?.$?.fn?.modal && el) {
          g.$(el).modal('show');
        }
      }
    }
  }

  fecharModal(): void {
    if (this.modal?.hide) {
      this.modal.hide();
    } else {
      const el = this.modalDetalhe?.nativeElement as HTMLElement;
      const g: any = window as any;
      if (g?.$?.fn?.modal && el) {
        g.$(el).modal('hide');
      }
    }

    this.detalheModificado = false;
    this.codigoCompraAtual = null;
  }

  carregarBeneficios(): void {
    this.spinner.show();

    this.svc.listarBeneficios().subscribe({
      next: (response: any) => {
        this.spinner.hide();

        if (response?.success && response?.result) {
          this.beneficios = response.result.map((beneficio: any) => ({
            value: beneficio.Codigo,
            label: beneficio.Descricao,
          }));
        } else {
          console.warn('Resposta da API sem dados válidos:', response);
          this.beneficios = [];
        }
      },
      error: (error) => {
        const mensagemErro = error.error?.msg;

        this.spinner.hide();
        Swal('Erro', mensagemErro, 'error');
      },
    });
  }
}
